import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { CATEGORIES, FEEDBACK_TYPES, FEEDBACK_SOURCES } from '../utils/constants';
import supabase from '../lib/supabase';

const { FiArrowLeft, FiSend, FiLoader, FiCheckCircle } = FiIcons;

const FeedbackForm = ({ onBack, preselectedCategory = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: preselectedCategory || '',
    feedbackType: '',
    source: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be logged in to submit feedback.');

      const { data, error: submitError } = await supabase
        .from('feedback_kb2024')
        .insert([
          {
            title: formData.title,
            description: formData.description,
            category: formData.category,
            feedback_type: formData.feedbackType,
            source: formData.source,
            author: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
            upvotes: 0
          }
        ])
        .select();

      if (submitError) {
        console.error('Supabase error:', submitError);
        throw submitError;
      }

      console.log('Feedback submitted successfully:', data);

      // Show success state
      setSuccess(true);

      // Reset form
      setFormData({
        title: '',
        description: '',
        category: preselectedCategory || '',
        feedbackType: '',
        source: ''
      });

      // Wait a bit to show success message, then navigate back
      setTimeout(() => {
        onBack();
      }, 2000);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError(`Failed to submit feedback: ${error.message || 'Please try again.'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (success) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center"
        >
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Feedback Submitted!</h2>
          <p className="text-gray-600 mb-4">Your feedback has been successfully submitted and will appear in the feedback section.</p>
          <div className="flex items-center justify-center gap-2 text-orange-600">
            <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
            <span>Redirecting to feedback...</span>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200"
          disabled={submitting}
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          Back to Feedback
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Submit Feedback</h1>
        <p className="text-gray-600">Help us improve by sharing your suggestions and reporting issues</p>
        {preselectedCategory && (
          <p className="text-sm text-orange-600 mt-2">Category: {preselectedCategory}</p>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-8"
      >
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Feedback Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              disabled={submitting}
              placeholder="Enter a brief, descriptive title for your feedback"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100"
            />
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              disabled={!!preselectedCategory || submitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100"
            >
              <option value="">Select a category</option>
              {CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="feedbackType" className="block text-sm font-medium text-gray-700 mb-2">
              Feedback Type *
            </label>
            <select
              id="feedbackType"
              name="feedbackType"
              value={formData.feedbackType}
              onChange={handleChange}
              required
              disabled={submitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100"
            >
              <option value="">Select feedback type</option>
              {FEEDBACK_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-2">
              Source *
            </label>
            <select
              id="source"
              name="source"
              value={formData.source}
              onChange={handleChange}
              required
              disabled={submitting}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100"
            >
              <option value="">Select source</option>
              {FEEDBACK_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              disabled={submitting}
              rows={8}
              placeholder="Provide detailed information about your feedback. Be specific about what you'd like to see improved, what issues you encountered, or what suggestions you have."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 resize-none disabled:bg-gray-100"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onBack}
              disabled={submitting}
              className="flex-1 py-3 px-6 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                  Submit Feedback
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default FeedbackForm;